import { Controller } from "@hotwired/stimulus"

// Periodically refreshes the <turbo-frame> this controller is attached to
// by issuing a Turbo-Frame fetch and swapping the frame's inner HTML with
// the matching frame from the response.
//
// We do the fetch manually (instead of setting `src` on the frame) to avoid
// Turbo's eager-load behavior on initial connect, which would otherwise
// clobber the server-rendered content before the first poll tick.
//
// Pauses while the tab is hidden, while a sortable drag is in progress, and
// while another refresh is in-flight.
//
// Usage (HAML):
//   %turbo-frame#trackers_table{
//     data: {
//       controller: "frame-refresh",
//       "frame-refresh-url-value": trackers_path,
//       "frame-refresh-interval-value": 10000
//     }
//   }
export default class extends Controller {
  static values = {
    url:      { type: String, default: "" },
    interval: { type: Number, default: 10000 }
  }

  connect() {
    this.handleVisibility = this.handleVisibility.bind(this)
    document.addEventListener("visibilitychange", this.handleVisibility)
    this.start()
  }

  disconnect() {
    this.stop()
    document.removeEventListener("visibilitychange", this.handleVisibility)
  }

  start() {
    this.stop()
    if (document.hidden) return
    if (this.intervalValue <= 0) return
    this.timer = setInterval(() => this.refresh(), this.intervalValue)
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  async refresh() {
    if (document.hidden) return
    if (this.inflight) return
    // Don't interrupt a sortable drag in progress.
    if (this.element.querySelector(".sortable-chosen, .sortable-ghost")) return

    const url = this.urlValue || window.location.pathname + window.location.search
    this.inflight = true

    try {
      const response = await fetch(url, {
        headers: {
          "Turbo-Frame": this.element.id,
          "Accept":      "text/html"
        },
        credentials: "same-origin"
      })
      if (!response.ok) return

      const html = await response.text()
      const doc  = new DOMParser().parseFromString(html, "text/html")
      const newFrame = doc.querySelector(`turbo-frame#${CSS.escape(this.element.id)}`)
      if (!newFrame) return

      this.element.innerHTML = newFrame.innerHTML
      // Mirror Turbo's native event so other listeners (e.g. tooltip init)
      // can re-initialize widgets inside the refreshed frame.
      this.element.dispatchEvent(new CustomEvent("turbo:frame-load", { bubbles: true }))
    } catch (err) {
      console.error("frame-refresh failed:", err)
    } finally {
      this.inflight = false
    }
  }

  handleVisibility() {
    if (document.hidden) {
      this.stop()
    } else {
      this.start()
    }
  }
}
